import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Stack from 'react-bootstrap/Stack';

import { TELOS_EVM_CHAIN } from '../config';
import ToastMessage from '../Widgets/ToastMessage';
import commonContextsWrapper from '../Helpers/commonContextsWrapper';

class LoginModal extends React.Component {
    get sharedState() {
        return this.props.sharedState;
    }

    get isLoggedIn() {
        return this.sharedState?.[1]?.isLoggedIn;
    }

    get loginWharf() {
        return this.sharedState?.[1]?.loginWharf;
    }

    get loginMetaMask() {
        return this.sharedState?.[1]?.loginMetaMask;
    }

    get logout() {
        return this.sharedState?.[1]?.logout;
    }

    get evmSession() {
        return this.sharedState?.[0]?.evmSession;
    }

    constructor(props) {
        super(props);

        this.state = {
            isSwitchingChains: false,
            errorMessage: undefined
        };

        this.switchToTelos = this.switchToTelos.bind(this);
        this.hideErrorMessage = this.hideErrorMessage.bind(this);
        this.onAccountsChanged = this.onAccountsChanged.bind(this);
        this.onChainChanged = this.onChainChanged.bind(this);
        this.close = this.close.bind(this);
    }

    // Actions
    switchToTelos() {
        if (this.state.isSwitchingChains) {
            return;
        }
        
        this.setState({isSwitchingChains: true});

        this.evmSession.switchChain(TELOS_EVM_CHAIN.chainID)
            .then(() => this.loginMetaMask()) // Re-log in again automatically
            .catch(error => {
                this.showErrorMessage(error);
                this.logout();
            })
            .finally(() => this.setState({isSwitchingChains: false}));
    };

    // Components
    modalBodyContent() {
        if (this.evmSession && !this.evmSession.isNetworkSupported) {
            if (this.state.isSwitchingChains) {
                return (
                    <Stack style={{alignItems: 'center', maring: '1rem 0 1rem 0', gap: '1rem'}}>
                        <Spinner animation="border" role="status" aria-hidden="true" style={{width: '2.75rem', height: '2.75rem'}}/>
                        <div style={{fontWeight: 300}}>Switching chains...</div>
                    </Stack>
                );
            }

            return (
                <Stack style={{gap: '0.5rem'}}>
                    <p style={{textAlign: 'center', marginBottom: '1rem'}}>
                        Your wallet is not connected to a supported network.
                    </p>
                        
                    <Stack direction="vertical" style={{gap: '0.5rem'}}>
                        <Button variant="primary" onClick={this.switchToTelos}>
                            Switch to Telos
                        </Button>

                        <Button variant="secondary" onClick={this.close}>Cancel</Button>
                    </Stack>
                </Stack>
            );
        }

        return (
            <Stack style={{gap: '0.5rem'}}>
                <Stack direction="vertical" style={{gap: '0.5rem'}}>
                    <Button variant="primary" disabled={this.isLoggedIn} onClick={this.loginWharf}>Telos Zero</Button>
                    <Button variant="primary" disabled={this.isLoggedIn} onClick={this.loginMetaMask}>Telos EVM</Button>
                    <Button variant="secondary" style={{marginTop: '1rem'}} onClick={this.close}>Cancel</Button>
                </Stack>
            </Stack>
        );
    };

    // Error Message Toast
    showErrorMessage(error) {
        if (error.code === 'ACTION_REJECTED') {
            return;
        }

        const internalError = error.info?.error;
        const errorMessage = (internalError?.data?.message || internalError?.message || error.message);
        
        this.setState({errorMessage: errorMessage});
    };

    hideErrorMessage() {
        this.setState({errorMessage: undefined});
    };

    // Misc
    onChainChanged(hexChainID) {
        const chainID = Number(hexChainID);

        if (this.evmSession && chainID !== TELOS_EVM_CHAIN.chainID) {
            this.logout();
        }
    };

    onAccountsChanged(accounts) {
        if (this.evmSession && this.evmSession.address !== accounts[0]) {
            this.logout();
        }
    };

    close() {
        this.props.onCloseClicked();
    };

    // React
    componentDidMount() {
        window.ethereum?.on('chainChanged', this.onChainChanged);
        window.ethereum?.on('accountsChanged', this.onAccountsChanged);
    };

    componentWillUnmount() {
        window.ethereum?.removeListener('chainChanged', this.onChainChanged);
        window.ethereum?.removeListener('accountsChanged', this.onAccountsChanged);
    };

    componentDidUpdate(prevProps) {
        if (prevProps.isShown !== this.props.isShown) {
            if (!this.props.isShown) {
                // If the user cancelled the modal without switching
                // chains reset the session (log the user out)
                if (this.evmSession && !this.evmSession.isNetworkSupported) {
                    this.logout();
                }

                this.setState({
                    isSwitchingChains: false,
                    errorMessage: undefined        
                });
            }
        }

        // If the user logged in, hide the modal automatically
        if (this.props.isShown && this.isLoggedIn) {
            this.close();
        }
    };

    render() {
        return (
            <>
                <ToastMessage.Error
                    isShown={!!this.state.errorMessage}
                    message={this.state.errorMessage}
                    onAutoClosed={this.hideErrorMessage}
                />

                <Modal show={this.props.isShown} onHide={this.close} size="sm" contentClassName="modal-content-dark" backdrop="static" centered>
                    <Modal.Header style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                        <Modal.Title>Login</Modal.Title>
                        <div style={{fontWeight: 300, fontSize: '0.8rem', color: 'var(--white-inactive)'}}>Select blockchain to log in with</div>
                    </Modal.Header>

                    <Modal.Body>{this.modalBodyContent()}</Modal.Body>
                </Modal>
            </>
        );
    };
}

export default commonContextsWrapper(LoginModal);
